import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from './AuthContext';
import { markTrialWelcomePending } from '../subscription/trialWelcomeStorage';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const codeFromUrl = searchParams.get('code') ?? '';
  const { refreshSession, verifyEmailCode, user } = useAuth();
  const [code, setCode] = useState(codeFromUrl);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(status === 'verified');

  useEffect(() => {
    if (status === 'verified') {
      markTrialWelcomePending();
      setVerified(true);
      void refreshSession();
    }
  }, [refreshSession, status]);

  useEffect(() => {
    if (!codeFromUrl || status || verified) {
      return;
    }

    setSubmitting(true);
    void verifyEmailCode(codeFromUrl)
      .then(() => {
        setVerified(true);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Unable to verify email.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [codeFromUrl, status, verified, verifyEmailCode]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await verifyEmailCode(trimmed);
      setVerified(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to verify email.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitting && codeFromUrl && !error && !verified) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Verifying your email…</Typography>
      </Container>
    );
  }

  if (verified) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Email confirmed
        </Typography>
        <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
          Your email is verified. Your free trial is now active.
        </Alert>
        <Button component={RouterLink} to="/explorer" variant="contained">
          Continue
        </Button>
      </Container>
    );
  }

  let title = 'Confirm your email';
  let message = 'Check your inbox and junk folder for a 6-digit code.';
  let severity: 'info' | 'error' = 'info';

  if (status === 'expired') {
    title = 'Code expired';
    message = 'This verification code has expired. Sign in and request a new one.';
    severity = 'error';
  } else if (status === 'invalid') {
    title = 'Invalid code';
    message = 'This verification code is invalid. Sign in and request a new one.';
    severity = 'error';
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h4" gutterBottom textAlign="center">
        {title}
      </Typography>
      <Alert severity={severity} sx={{ mb: 3 }}>
        {message}
        {user?.email ? ` (${user.email})` : ''}
      </Alert>
      {!status && (
        <Box component="form" onSubmit={(event) => void handleSubmit(event)}>
          <Stack spacing={2}>
            <TextField
              label="6-digit code"
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 6,
              }}
              fullWidth
              autoFocus
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || code.length !== 6}
            >
              {submitting ? 'Verifying…' : 'Verify email'}
            </Button>
          </Stack>
        </Box>
      )}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button component={RouterLink} to="/login" variant="text">
          Go to sign in
        </Button>
      </Box>
    </Container>
  );
};

export default VerifyEmailPage;
